import { useState, useEffect, useRef, useCallback } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { 
  MessageCircle, Send, Hash, Megaphone, ShoppingBag,
  Crown, Loader2, RefreshCw, Trash2
} from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL || '';
const TOKEN_KEY = 'fruitCrush_token';

// Channel icons
const CHANNEL_ICONS = {
  general: MessageCircle,
  trading: ShoppingBag,
  updates: Megaphone
};

// Channel colors
const CHANNEL_COLORS = {
  general: "from-blue-500 to-cyan-500",
  trading: "from-amber-500 to-orange-500",
  updates: "from-purple-500 to-pink-500"
};

export function ChatBox({ currentUser }) {
  const [channels, setChannels] = useState([]);
  const [activeChannel, setActiveChannel] = useState("general");
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [error, setError] = useState(null);
  const messagesEndRef = useRef(null);
  const pollIntervalRef = useRef(null);

  // Fetch channels
  const fetchChannels = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/channels`);
      if (response.ok) {
        const data = await response.json();
        setChannels(data.channels || []);
      }
    } catch (err) {
      console.error('Failed to fetch channels:', err);
    }
  }, []);

  // Fetch messages for active channel
  const fetchMessages = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${activeChannel}`);
      if (response.ok) {
        const data = await response.json();
        setMessages(data.messages || []);
        setError(null);
      }
    } catch (err) {
      console.error('Failed to fetch messages:', err);
      setError('Failed to load messages');
    } finally {
      setIsLoading(false);
    }
  }, [activeChannel]);

  // Initial load
  useEffect(() => {
    fetchChannels();
  }, [fetchChannels]);

  // Fetch messages when channel changes
  useEffect(() => {
    setIsLoading(true);
    setMessages([]);
    fetchMessages();

    // Poll for new messages every 3 seconds
    pollIntervalRef.current = setInterval(fetchMessages, 3000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
    };
  }, [activeChannel, fetchMessages]);

  // Scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Send message
  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || isSending) return;

    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) {
      setError('Please login to send messages');
      return;
    }

    setIsSending(true);
    try {
      const response = await fetch(`${API_URL}/api/chat/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          channel: activeChannel,
          content: newMessage.trim()
        })
      });

      if (response.ok) {
        setNewMessage('');
        fetchMessages(); // Refresh messages
      } else {
        const data = await response.json();
        setError(data.detail || 'Failed to send message');
      }
    } catch (err) {
      console.error('Failed to send message:', err);
      setError('Failed to send message');
    } finally {
      setIsSending(false);
    }
  };

  // Delete message
  const handleDeleteMessage = async (messageId) => {
    const token = localStorage.getItem(TOKEN_KEY);
    if (!token) return;

    try {
      const response = await fetch(`${API_URL}/api/chat/messages/${messageId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        fetchMessages();
      }
    } catch (err) {
      console.error('Failed to delete message:', err);
    }
  };

  // Check if user can delete a message
  const canDeleteMessage = (message) => {
    if (!currentUser) return false;
    return message.user_id === currentUser.id || currentUser.is_admin;
  };

  // Check if user can post in channel
  const canPostInChannel = () => {
    const channel = channels.find(c => c.id === activeChannel);
    if (!channel) return true;
    if (!channel.adminOnly) return true;
    return currentUser?.is_admin;
  };

  // Format timestamp
  const formatTime = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);

    if (date.toDateString() === today.toDateString()) {
      return 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      return 'Yesterday';
    }
    return date.toLocaleDateString();
  };

  // Group messages by date
  const groupedMessages = messages.reduce((groups, message) => {
    const date = formatDate(message.created_at);
    if (!groups[date]) {
      groups[date] = [];
    }
    groups[date].push(message);
    return groups;
  }, {});

  return (
    <div className="flex flex-col h-[500px] bg-gradient-to-b from-slate-900/95 to-slate-950/95 rounded-xl overflow-hidden">
      {/* Channel Tabs */}
      <div className="flex gap-1 p-2 bg-black/30 border-b border-white/10">
        {channels.map((channel) => {
          const IconComponent = CHANNEL_ICONS[channel.id] || Hash;
          const isActive = activeChannel === channel.id;
          
          return (
            <Button
              key={channel.id}
              variant={isActive ? "default" : "ghost"}
              size="sm"
              onClick={() => setActiveChannel(channel.id)}
              className={`
                flex items-center gap-1.5 px-3 py-1.5 rounded-lg transition-all
                ${isActive 
                  ? `bg-gradient-to-r ${CHANNEL_COLORS[channel.id]} text-white shadow-lg` 
                  : 'text-white/60 hover:text-white hover:bg-white/10'
                }
              `}
              data-testid={`chat-channel-${channel.id}`}
            >
              <IconComponent className="w-4 h-4" />
              <span className="text-sm font-medium">{channel.name}</span>
              {channel.adminOnly && (
                <Crown className="w-3 h-3 text-yellow-400" />
              )}
            </Button>
          );
        })}
      </div>

      {/* Messages Area */}
      <ScrollArea className="flex-1 p-3">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <Loader2 className="w-8 h-8 animate-spin text-white/50" />
          </div>
        ) : messages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-full text-white/40">
            <MessageCircle className="w-12 h-12 mb-2" />
            <p>No messages yet</p>
            <p className="text-sm">Be the first to say something!</p>
          </div>
        ) : (
          <div className="space-y-4">
            {Object.entries(groupedMessages).map(([date, msgs]) => (
              <div key={date}>
                {/* Date separator */}
                <div className="flex items-center gap-2 my-3">
                  <div className="flex-1 h-px bg-white/10" />
                  <span className="text-xs text-white/40 px-2">{date}</span>
                  <div className="flex-1 h-px bg-white/10" />
                </div>
                
                {/* Messages for this date */}
                {msgs.map((message) => (
                  <div
                    key={message.id}
                    className={`
                      group flex items-start gap-2 p-2 rounded-lg transition-all
                      ${message.is_admin ? 'bg-purple-500/10 border border-purple-500/20' : 'hover:bg-white/5'}
                    `}
                    data-testid={`chat-message-${message.id}`}
                  >
                    {/* Avatar placeholder */}
                    <div className={`
                      w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold
                      ${message.is_admin 
                        ? 'bg-gradient-to-br from-purple-500 to-pink-500' 
                        : 'bg-gradient-to-br from-blue-500 to-cyan-500'
                      }
                    `}>
                      {message.player_name?.charAt(0)?.toUpperCase() || '?'}
                    </div>
                    
                    {/* Message content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`font-semibold text-sm ${message.is_admin ? 'text-purple-400' : 'text-white'}`}>
                          {message.player_name}
                        </span>
                        {message.is_admin && (
                          <Badge className="bg-purple-500/30 text-purple-300 text-xs px-1.5 py-0">
                            <Crown className="w-3 h-3 mr-1" />
                            Admin
                          </Badge>
                        )}
                        <span className="text-xs text-white/30">
                          {formatTime(message.created_at)}
                        </span>
                      </div>
                      <p className="text-white/80 text-sm break-words mt-0.5">
                        {message.content}
                      </p>
                    </div>
                    
                    {/* Delete button */}
                    {canDeleteMessage(message) && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDeleteMessage(message.id)}
                        className="opacity-0 group-hover:opacity-100 transition-opacity h-6 w-6 text-white/30 hover:text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-3 h-3" />
                      </Button>
                    )}
                  </div>
                ))}
              </div>
            ))}
            <div ref={messagesEndRef} />
          </div>
        )}
      </ScrollArea>

      {/* Error message */}
      {error && (
        <div className="px-3 py-2 bg-red-500/20 border-t border-red-500/30">
          <p className="text-red-400 text-sm">{error}</p>
        </div>
      )}

      {/* Message Input */}
      <form onSubmit={handleSendMessage} className="p-3 bg-black/30 border-t border-white/10">
        {canPostInChannel() ? (
          <div className="flex gap-2">
            <Input
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder={`Message #${activeChannel}...`}
              className="flex-1 bg-white/10 border-white/20 text-white placeholder:text-white/40"
              maxLength={500}
              disabled={isSending}
              data-testid="chat-input"
            />
            <Button
              type="submit"
              disabled={!newMessage.trim() || isSending}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600"
              data-testid="chat-send-btn"
            >
              {isSending ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <Send className="w-4 h-4" />
              )}
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-center gap-2 text-white/40 py-2">
            <Crown className="w-4 h-4 text-yellow-500" />
            <span className="text-sm">Only admins can post in this channel</span>
          </div>
        )}
      </form>
    </div>
  );
}

export default ChatBox;
